/**
 * Frontend optimized biometric parameter transformation
 * This mirrors the backend optimization but is designed for client-side use
 */

import { biometricCategories } from './biometricParameters';

// Cache for parameter mappings
let parameterMappingCache = null;

/**
 * Initialize or get cached parameter mappings
 */
function getParameterMappingCache() {
  if (parameterMappingCache === null) {
    console.log('[CACHE] Initializing frontend biometric parameter mapping cache...');
    
    const rawToStandardizedMap = new Map();
    const standardizedToRawMap = new Map();
    
    // Pre-compute all mappings
    biometricCategories.forEach(category => {
      category.parameters.forEach(param => {
        const rawKey = param.key;
        const standardizedKey = `biometrics_${category.key}_${param.key}`;
        
        rawToStandardizedMap.set(rawKey, standardizedKey);
        standardizedToRawMap.set(standardizedKey, rawKey);
      });
    });
    
    parameterMappingCache = {
      rawToStandardized: rawToStandardizedMap,
      standardizedToRaw: standardizedToRawMap,
      initialized: Date.now()
    };
    
    console.log(`[CACHE] Frontend cache initialized with ${rawToStandardizedMap.size} parameter mappings`);
  }
  
  return parameterMappingCache;
}

/**
 * Optimized transformation from raw parameter names to standardized format
 * @param {Object} rawData - Object with raw parameter names
 * @returns {Object} - Object with standardized parameter names
 */
export function transformToStandardizedOptimized(rawData) {
  if (!rawData || typeof rawData !== 'object') {
    return rawData;
  }
  
  const cache = getParameterMappingCache();
  const standardizedData = {};
  
  for (const [rawKey, value] of Object.entries(rawData)) {
    const standardizedKey = cache.rawToStandardized.get(rawKey);
    if (standardizedKey) {
      standardizedData[standardizedKey] = value;
    } else {
      // Keep unknown parameters as-is
      standardizedData[rawKey] = value;
    }
  }
  
  return standardizedData;
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  const cache = getParameterMappingCache();
  
  return {
    initialized: new Date(cache.initialized).toISOString(),
    totalMappings: cache.rawToStandardized.size
  };
}
